
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Plus, Search, Calendar, IndianRupee, Users, Eye, Trash2, Edit, Sparkles, Filter, TrendingUp, Star, Zap, X, Bell, BarChart, ChevronDown } from "lucide-react";
import { jwtDecode } from "jwt-decode";
import apiService from "../services/api";
import AlertBox from "../../Components/AlertBox";

const ProjectList = ({ projects = [], setProjects, onNavigate }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoaded, setIsLoaded] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hoveredStat, setHoveredStat] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [localProjects, setLocalProjects] = useState(projects);
  const [milestones, setMilestones] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [selectedProjectNotifications, setSelectedProjectNotifications] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  // Change Status modal state
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusForm, setStatusForm] = useState({ projectId: null, status: '', description: '', files: [] });
  const MAX_STATUS_FILE_BYTES = 5 * 1024 * 1024; // 5MB per file
  const ALLOWED_STATUS_MIME = [
    'image/jpeg',
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain'
  ];

  // ---------- Search & Select Projects States ----------
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [showProjectSelector, setShowProjectSelector] = useState(false);

  // ---------- Advanced Filters States ----------
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [customerNameFilter, setCustomerNameFilter] = useState('all');
  const [projectNameFilter, setProjectNameFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [stateFilter, setStateFilter] = useState('all');
  const [projectHeadFilter, setProjectHeadFilter] = useState('all');
  const [projectStatusFilter, setProjectStatusFilter] = useState('all');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(true);
  const [departmentsLoading, setDepartmentsLoading] = useState(true);
  const [showAlert, setShowAlert] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);

  // Get URL search parameters
  const [searchParams] = useSearchParams();

  // Get employee filter from both URL parameters and navigation state
  const employeeFilter = location.state?.employeeFilter ||
    (searchParams.get('employeeId') ? {
      id: searchParams.get('employeeId'),
      name: searchParams.get('employeeName') || 'Unknown Employee'
    } : null);

  // Function to fetch custom statuses from API
  const fetchCustomStatuses = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('https://greenyellow-nightingale-241896.hostingersite.com/api/prjCustomStatus/', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const statuses = await response.json();
        console.log('ProjectList - API Response:', statuses);

        // Handle wrapped response format {statuses: Array}
        let statusArray = [];
        if (Array.isArray(statuses)) {
          statusArray = statuses;
        } else if (statuses && statuses.statuses && Array.isArray(statuses.statuses)) {
          console.log('ProjectList - Found wrapped statuses array');
          statusArray = statuses.statuses;
        } else {
          console.log('ProjectList - Invalid response format:', statuses);
          setStatusOptions([]);
          return;
        }

        if (statusArray.length > 0) {
          // Extract status names from the array
          const statusNames = statusArray.map(status => {
            // Try to get the name field
            if (status && typeof status === 'object' && status.name) {
              return status.name;
            }
            return null;
          }).filter(name => name); // Remove null values

          console.log('ProjectList - Extracted status names:', statusNames);
          setStatusOptions(statusNames);
        } else {
          console.log('ProjectList - Empty status array');
          setStatusOptions([]);
        }
      } else {
        console.error('ProjectList - API Error:', response.status);
        setStatusOptions([]);
      }
    } catch (error) {
      console.error('ProjectList - Fetch Error:', error);
      setStatusOptions([]);
    }
  };

  // ---------- Change Status Handlers ----------
  const openStatusModal = (projectId, currentStatus) => {
    setStatusForm({ projectId, status: '', description: '', files: [] });
    setShowStatusModal(true);
  };

  const handleStatusFileChange = (e) => {
    const incoming = Array.from(e.target.files || []);
    const valid = incoming.filter(f => {
      const typeOk = ALLOWED_STATUS_MIME.includes(f.type) || /\.(jpe?g|pdf|docx|txt)$/i.test(f.name);
      const sizeOk = f.size <= MAX_STATUS_FILE_BYTES;
      return typeOk && sizeOk;
    });
    setStatusForm(prev => ({ ...prev, files: valid }));
  };

  const submitStatusChange = async () => {
    if (!statusForm.projectId || !statusForm.status) return;
    try {
      await apiService.updateProject(statusForm.projectId, {
        status: statusForm.status,
        statusNote: statusForm.description,
      });
      await fetchProjects();
      setShowStatusModal(false);
    } catch (err) {
      console.error('Failed to update project status:', err);
    }
  };

  // Fetch projects from API
  const fetchProjects = async () => {
    try {
      console.log('ProjectList - fetchProjects called');
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.error('No access token found');
        return;
      }

      const decodedToken = jwtDecode(token);
      console.log('ProjectList - Decoded token:', decodedToken);

      const userRole = decodedToken.role;
      const userId = decodedToken.id || decodedToken._id;
      const companyId = decodedToken.role === 'company'
        ? (decodedToken.companyId || decodedToken.id)
        : decodedToken.companyId;

      const response = await fetch(`https://greenyellow-nightingale-241896.hostingersite.com/api/projectsmgnt`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        let data = await response.json();
        console.log('ProjectList - Fetched projects (before filter):', data.length, 'projects');
        console.log('ProjectList - User role:', userRole, 'User ID:', userId);

        // Filter projects based on user role
        if (userRole === 'employee' || userRole === 'team_member') {
          // Team member: only show projects they are assigned to
          data = data.filter(project => {
            // Check if user is in teamMembers array
            const isTeamMember = project.teamMembers && Array.isArray(project.teamMembers) &&
              project.teamMembers.some(member => {
                const memberId = typeof member === 'object' ? (member._id || member.id) : member;
                return String(memberId) === String(userId);
              });

            console.log(`Project "${project.title || project.name}" - isTeamMember:`, isTeamMember);
            return isTeamMember;
          });

          console.log('ProjectList - Filtered projects for team member:', data.length, 'projects');
        } else {
          // Company/Admin: show all projects (no filtering)
          console.log('ProjectList - Company/Admin user, showing all projects');
        }

        setLocalProjects(data);
        setProjects(data); // Also update parent state
        console.log('ProjectList - Updated localProjects and parent projects');
      } else {
        console.error('Failed to fetch projects:', response.status);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  // Fetch milestones/project-tasks from API
  const fetchMilestones = async () => {
    try {
      console.log('🚀 ProjectList - fetchMilestones called');
      const data = await apiService.getMilestones();
      console.log('📊 Milestones data received:', data);
      console.log('📊 Is array?', Array.isArray(data));
      setMilestones(Array.isArray(data) ? data : []);
      console.log('✅ Milestones set in state');
    } catch (error) {
      console.error('❌ Error fetching milestones:', error);
      setMilestones([]);
    }
  };

  // Fetch employees for name resolution
  const fetchEmployees = async () => {
    try {
      console.log('👥 ProjectList - fetchEmployees called');
      const data = await apiService.getEmployees();
      console.log('👥 Employees data received:', data);
      setEmployees(Array.isArray(data) ? data : []);
      console.log('✅ Employees set in state');
    } catch (error) {
      console.error('❌ Error fetching employees:', error);
      setEmployees([]);
    }
  };

  // Helper function to get project name for milestone
  const getProjectNameForMilestone = (milestone) => {
    // If projectName is already set, use it
    if (milestone.projectName) {
      return milestone.projectName;
    }

    // Try to find project by ID

    let projectId = null;
    if (milestone.project && typeof milestone.project === 'object' && milestone.project._id) {
      projectId = milestone.project._id;
    } else if (milestone.project && typeof milestone.project === 'string') {
      projectId = milestone.project;
    } else if (milestone.projectId) {
      projectId = milestone.projectId;
    }

    if (projectId) {
      const project = localProjects.find(p => p._id === projectId || p.id === projectId);
      if (project) {
        return project.title || project.name || 'Untitled Project';
      }
    }

    return 'Unknown Project';
  };

  // Helper function to get employee name from ID (handles both single and multiple employees)
  const getEmployeeName = (employeeData) => {
    console.log('getEmployeeName called with:', employeeData);

    if (!employeeData) return 'Unassigned';

    // Handle if employeeData is an array (multiple employees)
    if (Array.isArray(employeeData)) {
      console.log('📋 Employee data is array:', employeeData);

      if (employeeData.length === 0) return 'Unassigned';

      const employeeNames = employeeData.map(empData => {
        // Handle each employee in the array
        if (typeof empData === 'object') {
          // Try different name fields
          const possibleNames = [
            empData.name,
            empData.firstName,
            empData.teamMemberName,
            empData.fullName,
            empData.employeeName
          ];

          for (const name of possibleNames) {
            if (name && typeof name === 'string' && name.trim()) {
              return name.trim();
            }
          }

          // Look up by ID if no name found in object
          const employeeId = empData._id || empData.id;
          if (employeeId) {
            const employee = employees.find(emp =>
              String(emp._id) === String(employeeId) ||
              String(emp.id) === String(employeeId)
            );
            if (employee) {
              return employee.teamMemberName || employee.name || employee.firstName || 'Unknown Employee';
            }
          }
          return 'Unknown Employee';
        } else if (typeof empData === 'string') {
          // Look up string ID
          const employee = employees.find(emp =>
            String(emp._id) === String(empData) ||
            String(emp.id) === String(empData)
          );
          if (employee) {
            return employee.teamMemberName || employee.name || employee.firstName || 'Unknown Employee';
          }
          return 'Unknown Employee';
        }
        return 'Unknown Employee';
      }).filter(name => name !== 'Unknown Employee');

      if (employeeNames.length > 0) {
        console.log('✅ Found employee names from array:', employeeNames);
        return employeeNames.join(', ');
      }
      return 'Multiple Employees (Unknown)';
    }

    // Handle if employeeData is already an object with employee details
    if (typeof employeeData === 'object') {
      console.log('📋 Employee data is object:', employeeData);

      // Try different name fields that might exist in the populated object
      const possibleNames = [
        employeeData.name,
        employeeData.firstName,
        employeeData.teamMemberName,
        employeeData.fullName,
        employeeData.employeeName,
        // Handle nested structures
        employeeData.employee?.name,
        employeeData.employee?.firstName,
        employeeData.employee?.teamMemberName
      ];

      for (const name of possibleNames) {
        if (name && typeof name === 'string' && name.trim()) {
          console.log('✅ Found employee name:', name);
          return name.trim();
        }
      }

      // If it's an object but no name found, try to get the ID and look it up
      const employeeId = employeeData._id || employeeData.id;
      if (employeeId) {
        console.log('Looking up employee by ID from object:', employeeId, 'in employees:', employees.length);
        const employee = employees.find(emp =>
          String(emp._id) === String(employeeId) ||
          String(emp.id) === String(employeeId)
        );

        if (employee) {
          const foundName = employee.teamMemberName || employee.name || employee.firstName || 'Unknown Employee';
          console.log('✅ Found employee by lookup:', foundName);
          return foundName;
        } else {
          console.log('❌ No employee found with ID:', employeeId, 'Available employees:', employees.map(e => ({ id: e._id, name: e.teamMemberName || e.name })));
        }
      }

      console.log('⚠️ Could not extract name from object, showing object info');
      return `Employee (${employeeData._id || employeeData.id || 'Unknown ID'})`;
    }

    // Handle if employeeData is a string ID
    if (typeof employeeData === 'string') {
      console.log('Looking up employee by string ID:', employeeData, 'in employees:', employees.length);
      const employee = employees.find(emp =>
        String(emp._id) === String(employeeData) ||
        String(emp.id) === String(employeeData)
      );

      if (employee) {
        const foundName = employee.teamMemberName || employee.name || employee.firstName || 'Unknown Employee';
        console.log('✅ Found employee by string lookup:', foundName);
        return foundName;
      } else {
        console.log('❌ No employee found with string ID:', employeeData, 'Available employees:', employees.map(e => ({ id: e._id, name: e.teamMemberName || e.name })));
      }

      return `Employee ID: ${employeeData}`;
    }

    console.log('⚠️ Unexpected employee data type:', typeof employeeData);
    return 'Unknown Assignment';
  };

  // Calculate milestones due/overdue and within 1 day for a specific project
  const getDueMilestones = (projectId, projectName) => {
    console.log('getDueMilestones called with:', { projectId, projectName });
    console.log('📋 Total milestones available:', milestones.length);

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Start of today
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);
    tomorrow.setHours(23, 59, 59, 999); // End of tomorrow

    console.log('… Current date/time:', today);
    console.log('… Date range for notifications:', { today, tomorrow });

    const dueTasks = milestones.filter(milestone => {
      console.log('Ž Checking milestone for due status:', milestone);

      // Check if milestone belongs to this project
      const belongsToProject = (
        (milestone.project && typeof milestone.project === 'object' && milestone.project._id === projectId) ||
        (milestone.project && milestone.project === projectId) ||
        (milestone.projectId && milestone.projectId === projectId) ||
        (milestone.projectName && (
          milestone.projectName === projectName ||
          milestone.projectName === projectId
        ))
      );

      console.log('—ï Belongs to project?', belongsToProject);

      if (!belongsToProject) return false;

      // Check if due date is overdue, due today, or due tomorrow
      const dueDate = new Date(milestone.dueDate);
      const isInRange = dueDate <= tomorrow; // Includes overdue, today, and tomorrow
      const isNotCompleted = milestone.status !== 'Completed';

      console.log('… Due date check:', { dueDate, isInRange, isNotCompleted, status: milestone.status });

      return isInRange && isNotCompleted;
    });

    console.log('✅ Filtered milestones (overdue/today/tomorrow):', dueTasks);
    return dueTasks;
  };

  // Calculate upcoming project-tasks for a specific project (next 2 days)
  const getUpcomingSubTasks = (projectId, projectName) => {
    console.log('getUpcomingSubTasks called with:', { projectId, projectName });
    console.log('📋 Total milestones available:', milestones.length);

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Start of today
    const twoDaysFromNow = new Date();
    twoDaysFromNow.setDate(today.getDate() + 2);
    twoDaysFromNow.setHours(23, 59, 59, 999); // End of day after tomorrow

    console.log('… Date range:', { today, twoDaysFromNow });

    const filteredTasks = milestones.filter(milestone => {
      console.log('Ž Checking milestone:', milestone);

      // Check if milestone belongs to this project
      const belongsToProject = (
        (milestone.projectId && milestone.projectId === projectId) ||
        (milestone.projectName && (
          milestone.projectName === projectName ||
          milestone.projectName === projectId
        ))
      );

      console.log('—ï Belongs to project?', belongsToProject);

      if (!belongsToProject) return false;

      // Check if due date is within next 2 days
      const dueDate = new Date(milestone.dueDate);
      const isInDateRange = dueDate >= today && dueDate <= twoDaysFromNow;
      const isNotCompleted = milestone.status !== 'Completed';

      console.log('… Due date check:', { dueDate, isInDateRange, isNotCompleted, status: milestone.status });

      return isInDateRange && isNotCompleted;
    });

    console.log('✅ Filtered upcoming tasks:', filteredTasks);
    return filteredTasks;
  };

  // Fetch departments and statuses for name resolution
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        // Get token from localStorage
        const token = localStorage.getItem('authToken');

        let COMPANY_ID = '';
        if (token) {
          try {
            const decodedToken = jwtDecode(token); // Decode JWT
            // COMPANY_ID = decodedToken.companyId; // Extract the `id` field
            COMPANY_ID = decodedToken.role === 'company'
              ? (decodedToken.companyId || decodedToken.id)
              : decodedToken.companyId;


            console.log("Decoded Token:", decodedToken);
            console.log("Extracted Company ID:", COMPANY_ID);
          } catch (err) {
            console.error("Error decoding token:", err);
          }
        }

        const response = await fetch(`https://greenyellow-nightingale-241896.hostingersite.com/api/departments/company/${COMPANY_ID}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setDepartments(Array.isArray(data) ? data : []);
        } else {
          console.error('Failed to fetch departments:', response.status);
          setDepartments([]);
        }
      } catch (error) {
        console.error('Error fetching departments:', error);
        setDepartments([]);
      } finally {
        setDepartmentsLoading(false);
      }
    };

    fetchDepartments();
    fetchProjects(); // Initial fetch
    fetchMilestones(); // Fetch milestones for notifications
    fetchEmployees(); // Fetch employees for name resolution
    fetchCustomStatuses(); // Fetch custom statuses for filter dropdown
  }, []);

  // Reset other filters when department changes (Cascading Effect)
  useEffect(() => {
    if (departmentFilter !== 'all') {
      console.log('ProjectList - Department changed, resetting other filters');
      setCustomerNameFilter('all');
      setProjectNameFilter('all');
      setCityFilter('all');
      setStateFilter('all');
      setProjectHeadFilter('all');
      setProjectStatusFilter('all');
    }
  }, [departmentFilter]);

  // Update local projects when props change
  useEffect(() => {
    setLocalProjects(projects);
  }, [projects]);

  // Auto-refresh projects every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchProjects();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, []);

  // Expose refresh function globally for other components
  useEffect(() => {
    console.log('ProjectList - Setting up window.refreshProjects');
    window.refreshProjects = fetchProjects;
    return () => {
      console.log('ProjectList - Cleaning up window.refreshProjects');
      delete window.refreshProjects;
    };
  }, [fetchProjects]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".custom-dropdown-container")) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes gradientShift {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
      
      @keyframes slideInDown {
        from {
          opacity: 0;
          transform: translateY(-30px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(30px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      @keyframes cardSlideIn {
        from {
          opacity: 0;
          transform: translateX(-20px) scale(0.95);
        }
        to {
          opacity: 1;
          transform: translateX(0) scale(1);
        }
      }
      
      @keyframes rotate {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      
      @keyframes pulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.05); }
      }
      
      @keyframes progressGlow {
        0% { box-shadow: 0 0 10px rgba(102, 126, 234, 0.6); }
        100% { box-shadow: 0 0 20px rgba(102, 126, 234, 0.9), 0 0 30px rgba(240, 147, 251, 0.5); }
      }
      
      @keyframes textGlow {
        from { text-shadow: 0 0 10px rgba(102,126,234,0.3); }
        to { text-shadow: 0 0 20px rgba(102,126,234,0.6), 0 0 30px rgba(118,75,162,0.4); }
      }
      
      @keyframes cardFloat {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-3px); }
      }
      
      @keyframes staggerIn {
        from { opacity: 0; transform: translateY(30px) scale(0.95); }
        to { opacity: 1; transform: translateY(0) scale(1); }
      }
    `;
    document.head.appendChild(style);
    const t = setTimeout(() => setIsLoaded(true), 100);
    return () => {
      clearTimeout(t);
      document.head.removeChild(style);
    };
  }, []);

  // Escape key event listener for custom dropdowns
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setActiveDropdown(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // ---------- Modern Glassmorphism Styles ----------
  const projectListStyle = {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    fontFamily: '"Inter", "Segoe UI", Roboto, sans-serif'
  };

  const backgroundOverlayStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(20px)',
    zIndex: 0,
  };

  const contentWrapperStyle = {
    position: 'relative',
    zIndex: 1,
  };
  const pageHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '0.75rem',
    marginBottom: '1.5rem',
    padding: '1rem 1.25rem',
    backgroundColor: 'white',
    borderRadius: '0.5rem',
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    animation: 'slideInDown 0.8s ease-out',
  };

  const headerGlowStyle = {
    // position: 'absolute',
    // top: '-50%',
    // left: '-50%',
    // width: '200%',
    // height: '200%',
    // background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
    // animation: 'rotate 20s linear infinite',
  };

  const headerTitleStyle = {
    fontSize: '1.5rem',
    fontWeight: 900,
    margin: 0,
    color: '#1f2937',
    position: 'relative',
    zIndex: 2,
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap',
  };

  const headerIconsStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    position: 'relative',
    zIndex: 2,
    overflow: 'hidden',
    flex: '1 1 auto',
    minWidth: '150px',
  };

  const btnStyle = {
    display: "inline-flex",
    alignItems: "center",
    padding: "0.6rem 1rem",
    border: "none",
    borderRadius: "0.75rem",
    fontWeight: 700,
    textDecoration: "none",
    cursor: "pointer",
    transition: "all 0.4s cubic-bezier(0.4,0,0.2,1)",
    gap: "0.5rem",
    position: "relative",
    overflow: "hidden",
    backdropFilter: 'blur(15px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    fontSize: '0.9rem',
    whiteSpace: 'nowrap',
  };

  const btnPrimaryStyle = {
    ...btnStyle,
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    boxShadow: '0 10px 25px rgba(102, 126, 234, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
    position: 'relative',
    zIndex: 2,
  };

  const btnSmStyle = {
    padding: "0.5rem 0.75rem",
    fontSize: "0.875rem",
    borderRadius: "0.75rem",
  };

  const filtersSectionStyle = {
    display: "flex",
    gap: "2rem",
    marginBottom: "3rem",
    alignItems: "center",
    flexWrap: "wrap",
    backgroundColor: 'white',
    borderRadius: '0.5rem',
    padding: '1.5rem',
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  };

  const projectCardStyle = {
    backgroundColor: 'white',
    borderRadius: '0.5rem',
    padding: '2rem',
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
    cursor: 'pointer',
    position: 'relative',
    overflow: 'hidden',
    marginBottom: '2rem'
  };

  const searchBarStyle = {
    position: "relative",
    flex: 1,
    minWidth: "300px",
    marginRight: "2rem"
  };

  const searchInputStyle = {
    width: "100%",
    padding: "1rem 1rem 1rem 3rem",
    border: "1px solid #d1d5db",
    borderRadius: "0.5rem",
    fontSize: "1rem",
    outline: "none",
    backgroundColor: "white",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    color: "#1f2937",
    "::placeholder": {
      color: "#9ca3af",
    }
  };

  const searchIconStyle = {
    position: "absolute",
    left: "1rem",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#9ca3af"
  };

  const filterControlsStyle = {
    display: "flex",
    gap: "1rem",
    position: "relative",
    zIndex: 10
  };

  const selectStyle = {
    padding: "0.75rem 1rem",
    border: "1px solid #d1d5db",
    borderRadius: "0.5rem",
    fontSize: "0.9rem",
    backgroundColor: "white",
    color: "#1f2937",
    cursor: "pointer",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    outline: "none",
    appearance: "auto",
    WebkitAppearance: "menulist",
    MozAppearance: "menulist",
    pointerEvents: "auto",
    zIndex: 1000,
    position: "relative"
  };

  // ---------- Cascading Filter Options (Department-based) ----------
  const getFilteredFilterOptions = () => {
    // Helper function to safely extract string values from potentially object fields
    const extractStringValue = (value, fallback = 'Unknown') => {
      if (!value) return fallback;
      if (typeof value === 'string') return value;
      if (typeof value === 'object') {
        return value.name || value.title || value.teamMemberName || fallback;
      }
      return String(value);
    };

    // Helper function to check if project matches selected department
    const projectMatchesDepartment = (project) => {
      if (departmentFilter === 'all') return true;

      const projectDepartments = [
        project.department,
        project.dept,
        project.departmentName,
        project.category,
        project.projectType
      ];

      for (const dept of projectDepartments) {
        if (!dept) continue;

        if (Array.isArray(dept)) {
          const match = dept.some(d => {
            if (typeof d === 'object') {
              return String(d._id || d.id) === String(departmentFilter);
            }
            return String(d) === String(departmentFilter);
          });
          if (match) return true;
        }
        else if (typeof dept === 'object' && dept !== null) {
          if (String(dept._id || dept.id) === String(departmentFilter)) return true;
        }
        else if (typeof dept === 'string') {
          if (String(dept) === String(departmentFilter)) return true;

          const matchingDept = departments.find(d => d.name === String(dept));
          if (matchingDept && String(matchingDept._id || matchingDept.id) === String(departmentFilter)) {
            return true;
          }
        }
      }
      return false;
    };

    // Filter projects based on selected department
    const departmentFilteredProjects = localProjects.filter(projectMatchesDepartment);

    console.log(`ProjectList - Department '${departmentFilter}' filtered projects:`, departmentFilteredProjects.length, 'out of', localProjects.length);

    // Extract options only from department-filtered projects
    const customerNames = [...new Set(departmentFilteredProjects.map(p => {
      const customer = p.customerName || p.clientName || p.client || p.customer;
      return extractStringValue(customer);
    }).filter(v => v !== 'Unknown'))];

    const projectNames = [...new Set(departmentFilteredProjects.map(p => extractStringValue(p.title || p.name, 'Untitled')).filter(v => v !== 'Untitled'))];

    const cities = [...new Set(departmentFilteredProjects.map(p => {
      const city = p.clientCity || p.city || p.location?.city || p.projectLocation || p.address?.city ||
        p.clientAddress?.city || p.customerCity || p.projectCity;
      return extractStringValue(city);
    }).filter(v => v !== 'Unknown'))];

    const states = [...new Set(departmentFilteredProjects.map(p => {
      const state = p.clientState || p.state || p.location?.state || p.address?.state ||
        p.clientAddress?.state || p.customerState || p.projectState;
      return extractStringValue(state);
    }).filter(v => v !== 'Unknown'))];

    const projectHeads = [...new Set(departmentFilteredProjects.map(p => {
      const head = p.projectHead || p.projectManager || p.manager || p.lead;
      return extractStringValue(head);
    }).filter(v => v !== 'Unknown'))];

    const projectStatuses = [...new Set(departmentFilteredProjects.map(p => extractStringValue(p.status)).filter(v => v !== 'Unknown'))];

    return {
      departments: [], // Will be populated from departments API
      customerNames: customerNames.sort(),
      projectNames: projectNames.sort(),
      cities: cities.sort(),
      states: states.sort(),
      projectHeads: projectHeads.sort(),
      projectStatuses: projectStatuses.sort()
    };
  };

  const filterOptions = getFilteredFilterOptions();

  // state
  const [statusFilter, setStatusFilter] = useState("All");
  const [sortBy, setSortBy] = useState("name");

  // helpers to safely access fields (handles different API shapes)
  const getId = (p) => p?._id || p?.id || "";
  const getTitle = (p) => (p?.title || p?.name || "Untitled Project");
  const getDescription = (p) => (p?.description || "");
  const getDueDateRaw = (p) => (p?.dueDate || p?.deadline || p?.endDate || null);
  const getDueDate = (p) => {
    const raw = getDueDateRaw(p);
    if (!raw) return null;
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  };
  const getBudget = (p) => {
    if (p == null) return 0;
    if (typeof p.budget === "number") return p.budget;
    const n = Number(p.budget);
    return isNaN(n) ? 0 : n;
  };
  const getProgress = (p) => {
    const n = Number(p?.progress ?? 0);
    return isNaN(n) ? 0 : Math.max(0, Math.min(100, n));
  };

  // Calculate progress based on status position in statusOptions array
  const getStatusProgress = (currentStatus, statusOptions) => {
    if (!statusOptions || statusOptions.length === 0) {
      // Fallback to default statuses if no custom statuses available
      const defaultStatuses = ['Planning', 'In Progress', 'Completed'];
      const statusIndex = defaultStatuses.findIndex(status =>
        status.toLowerCase() === (currentStatus || '').toLowerCase()
      );
      const currentPosition = statusIndex >= 0 ? statusIndex + 1 : 1;
      return {
        current: currentPosition,
        total: defaultStatuses.length,
        percentage: Math.round((currentPosition / defaultStatuses.length) * 100)
      };
    }

    const statusIndex = statusOptions.findIndex(status =>
      status.toLowerCase() === (currentStatus || '').toLowerCase()
    );
    const currentPosition = statusIndex >= 0 ? statusIndex + 1 : 1;

    return {
      current: currentPosition,
      total: statusOptions.length,
      percentage: Math.round((currentPosition / statusOptions.length) * 100)
    };
  };
  const getDepartment = (p) => {
    // Handle populated department object or fallback to string ID
    if (p?.department) {
      // If department is populated (object with name property)
      if (typeof p.department === 'object' && p.department.name) {
        return p.department.name;
      }
      // If department is still just an ID string, try to resolve it to name
      if (typeof p.department === 'string' && p.department.trim() !== '') {
        const deptId = p.department.trim();
        const foundDept = departments.find(dept =>
          (dept._id && dept._id === deptId) ||
          (dept.id && dept.id === deptId)
        );
        return foundDept ? foundDept.name : deptId;
      }
    }
    return p?.team || "";
  };
  const getCreatedAt = (p) => p?.createdAt || p?.created_at || null;
  const getUpdatedAt = (p) => p?.updatedAt || p?.updated_at || null;

  // normalize various status formats to canonical display
  function normalizeStatus(raw) {
    if (!raw && raw !== 0) return "Unknown";
    const s = String(raw).trim();
    const lower = s.toLowerCase();
    if (["in progress", "in-progress", "inprogress"].includes(lower)) return "In Progress";
    if (["planning", "plan"].includes(lower)) return "Planning";
    if (["on hold", "on-hold", "onhold"].includes(lower)) return "On Hold";
    if (["completed", "complete", "done"].includes(lower)) return "Completed";
    if (["cancelled", "canceled"].includes(lower)) return "Cancelled";
    // fallback: capitalize first letter
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  const getStatusColor = (rawStatus) => {
    const status = normalizeStatus(rawStatus);
    const statusLower = status.toLowerCase();

    // Match colors from Reports component Project Status Distribution

    // Green color for completed status
    if (statusLower.includes('completed') || statusLower.includes('complete') || statusLower.includes('done') || statusLower.includes('finished')) {
      return '#10b981'; // emerald green
    }

    // Light pink for "In Process" status
    if (statusLower === 'in progress' || statusLower === 'in process' || statusLower.includes('process') || statusLower.includes('progress') || statusLower.includes('ongoing') || statusLower.includes('active')) {
      return '#D4AFD0'; // light pink
    }

    // Amber color for planning status
    if (statusLower.includes('planning') || statusLower.includes('plan') || statusLower.includes('initial')) {
      return '#f59e0b'; // amber
    }

    // Blue color for "Start" status
    if (statusLower.includes('start') && !statusLower.includes('restart')) {
      return '#3b82f6'; // blue
    }

    // Violet for "About to Complete" status
    if (statusLower === 'about to complete' || statusLower.includes('about to complete') || statusLower.includes('near completion') || statusLower.includes('almost done') || statusLower.includes('about to')) {
      return '#7032FF'; // violet
    }

    // Red for hold/cancelled statuses
    if (statusLower.includes('hold') || statusLower.includes('pause') || statusLower.includes('stop') || statusLower.includes('cancelled') || statusLower.includes('canceled')) {
      return '#ef4444'; // red
    }

    // Cyan for approval/pending statuses
    if (statusLower.includes('approval') || statusLower.includes('pending')) {
      return '#06b6d4'; // cyan
    }

    // Orange for other statuses
    if (statusLower.includes('review') || statusLower.includes('testing') || statusLower.includes('qa')) {
      return '#f97316'; // orange
    }

    // Default gray for unknown statuses
    return "#6b7280";
  };

  // ---------- Project Selection Handlers ----------
  const handleProjectToggle = (projectId) => {
    setSelectedProjects(prev => {
      if (prev.includes(projectId)) {
        return prev.filter(id => id !== projectId);
      } else {
        return [...prev, projectId];
      }
    });
  };

  const clearSelection = () => {
    setSelectedProjects([]);
    setSearchTerm('');
  };

  // Enhanced filtering logic with Advanced Filters
  const getFilteredProjects = () => {
    // Helper function to safely extract string values for filtering
    const extractStringValue = (value, fallback = 'Unknown') => {
      if (!value) return fallback;
      if (typeof value === 'string') return value;
      if (typeof value === 'object') {
        return value.name || value.title || value.teamMemberName || fallback;
      }
      return String(value);
    };

    return (localProjects || []).filter(project => {
      // Enhanced Search filter - searches through all Advanced Filter fields
      const searchMatch = !searchTerm || (() => {
        const searchLower = searchTerm.toLowerCase();

        // Project basic info
        const projectTitle = extractStringValue(project.title || project.name, '');
        const projectStatus = extractStringValue(project.status, '');

        // Department fields
        const departmentFields = [
          project.department,
          project.dept,
          project.departmentName,
          project.category,
          project.projectType
        ].map(field => extractStringValue(field, '')).join(' ');

        // Customer fields
        const customerFields = [
          project.customerName,
          project.clientName,
          project.client,
          project.customer
        ].map(field => extractStringValue(field, '')).join(' ');

        // City fields
        const cityFields = [
          project.clientCity,
          project.city,
          project.location?.city,
          project.projectLocation,
          project.address?.city,
          project.clientAddress?.city,
          project.customerCity,
          project.projectCity
        ].map(field => extractStringValue(field, '')).join(' ');

        // State fields
        const stateFields = [
          project.clientState,
          project.state,
          project.location?.state,
          project.address?.state,
          project.clientAddress?.state,
          project.customerState,
          project.projectState
        ].map(field => extractStringValue(field, '')).join(' ');

        // Project Head fields
        const projectHeadFields = [
          project.projectHead,
          project.projectManager,
          project.manager,
          project.lead
        ].map(field => extractStringValue(field, '')).join(' ');

        // Combine all searchable text
        const allSearchableText = [
          projectTitle,
          projectStatus,
          departmentFields,
          customerFields,
          cityFields,
          stateFields,
          projectHeadFields
        ].join(' ').toLowerCase();

        return allSearchableText.includes(searchLower);
      })();

      // Advanced filters - Department matching
      let departmentMatch = departmentFilter === 'all';

      if (!departmentMatch) {
        const projectDepartments = [
          project.department,
          project.dept,
          project.departmentName,
          project.category,
          project.projectType
        ];

        for (const dept of projectDepartments) {
          if (!dept) continue;

          if (Array.isArray(dept)) {
            departmentMatch = dept.some(d => {
              if (typeof d === 'object') {
                return String(d._id || d.id) === String(departmentFilter);
              }
              return String(d) === String(departmentFilter);
            });
          }
          else if (typeof dept === 'object' && dept !== null) {
            departmentMatch = String(dept._id || dept.id) === String(departmentFilter);
          }
          else if (typeof dept === 'string') {
            departmentMatch = String(dept) === String(departmentFilter);

            if (!departmentMatch) {
              const matchingDept = departments.find(d => d.name === String(dept));
              if (matchingDept) {
                departmentMatch = String(matchingDept._id || matchingDept.id) === String(departmentFilter);
              }
            }
          }

          if (departmentMatch) break;
        }
      }

      // Other Advanced Filters
      const customerMatch = customerNameFilter === 'all' ||
        [project.customerName, project.clientName, project.client, project.customer]
          .map(customer => extractStringValue(customer))
          .includes(customerNameFilter);

      const projectNameMatch = projectNameFilter === 'all' ||
        extractStringValue(project.title || project.name, 'Untitled') === projectNameFilter;

      const cityMatch = cityFilter === 'all' ||
        [project.clientCity, project.city, project.location?.city, project.projectLocation, project.address?.city,
        project.clientAddress?.city, project.customerCity, project.projectCity]
          .map(city => extractStringValue(city))
          .includes(cityFilter);

      const stateMatch = stateFilter === 'all' ||
        [project.clientState, project.state, project.location?.state, project.address?.state,
        project.clientAddress?.state, project.customerState, project.projectState]
          .map(state => extractStringValue(state))
          .includes(stateFilter);

      const projectHeadMatch = projectHeadFilter === 'all' ||
        [project.projectHead, project.projectManager, project.manager, project.lead]
          .map(head => extractStringValue(head))
          .includes(projectHeadFilter);

      const statusMatch = projectStatusFilter === 'all' ||
        extractStringValue(project.status) === projectStatusFilter;

      // Legacy filters
      const legacyStatusMatch = statusFilter === "All" || normalizeStatus(project?.status) === statusFilter;

      // Employee filter: if employee filter is active, only show projects assigned to that employee
      const matchesEmployee = !employeeFilter ||
        (Array.isArray(project.teamMembers) && project.teamMembers.some(member => {
          const memberId = typeof member === 'object' ? member._id : member;
          return String(memberId) === String(employeeFilter.id);
        }));

      return searchMatch && departmentMatch && customerMatch && projectNameMatch &&
        cityMatch && stateMatch && projectHeadMatch && statusMatch &&
        legacyStatusMatch && matchesEmployee;
    });
  };

  // Apply project selection filter if projects are selected
  const getDisplayProjects = () => {
    const advancedFilteredProjects = getFilteredProjects();

    // If projects are selected, show only selected projects from the filtered results
    if (selectedProjects.length > 0) {
      return advancedFilteredProjects.filter(project =>
        selectedProjects.includes(project._id || project.id)
      );
    }

    // Otherwise show all filtered projects
    return advancedFilteredProjects;
  };

  const filteredProjects = getDisplayProjects()
    .sort((a, b) => {
      switch (sortBy) {
        case "deadline": {
          const da = getDueDate(a);
          const db = getDueDate(b);
          return (da ? da.getTime() : 0) - (db ? db.getTime() : 0);
        }
        case "budget":
          return getBudget(b) - getBudget(a);
        case "status":
          return normalizeStatus(a?.status).localeCompare(normalizeStatus(b?.status));
        default:
          return getTitle(a).localeCompare(getTitle(b));
      }
    });

  const deleteProject = async (projectId) => {
    if (!projectId) return;

    // Find the project to get its name for milestone deletion
    const projectToDelete = localProjects.find(project => getId(project) === projectId);
    const projectName = projectToDelete ? getTitle(projectToDelete) : null;

    if (window.confirm("Are you sure you want to permanently delete this project? This will also delete all related project-tasks/milestones. This action cannot be undone.")) {
      try {
        console.log('Starting cascading delete for project:', { projectId, projectName });

        // First, delete all related milestones/project-tasks
        try {
          const deletedMilestonesCount = await apiService.deleteMilestonesByProject(projectId, projectName);
          console.log(`Deleted ${deletedMilestonesCount} milestones for project ${projectName}`);
        } catch (milestoneError) {
          console.warn('Error deleting milestones, continuing with project deletion:', milestoneError);
          // Continue with project deletion even if milestone deletion fails
        }

        // Then delete the project itself
        const token = localStorage.getItem('authToken');
        const response = await fetch(`https://greenyellow-nightingale-241896.hostingersite.com/api/projectsmgnt/${projectId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to delete project');
        }

        // Update local state
        if (typeof setProjects === "function") {
          setProjects(prev => (prev || []).filter(project => getId(project) !== projectId));
        }

        // Refresh related components if their refresh functions are available
        if (window.refreshProjectTracker) {
          console.log('Refreshing ProjectTracker after project deletion');
          window.refreshProjectTracker();
        }

        // Refresh other project-related components
        if (window.refreshProjects && window.refreshProjects !== fetchProjects) {
          console.log('Refreshing other project components');
          window.refreshProjects();
        }

        // Refresh milestone/project-task related components
        if (window.refreshMilestones) {
          console.log('Refreshing milestone components');
          window.refreshMilestones();
        }

        setShowAlert('Project and all related project-tasks deleted successfully');
      } catch (error) {
        console.error('Error deleting project:', error);
        setShowAlert('Failed to delete project. Please try again.');
      }
    }
  };

  const handleCardClick = (projectId) => {
    if (onNavigate) {
      // Store the project ID in the path for the FranchiseDashboard to handle
      onNavigate(`/projects/${projectId}`, 'Project Details');
    } else {
      navigate(`/projects/${projectId}`);
    }
  };

  const selectedDepartmentName = departmentFilter === 'all'
    ? 'All Departments'
    : (departments.find(d => String(d._id || d.id) === String(departmentFilter))?.name || 'All Departments');

  const selectedClientName = customerNameFilter === 'all'
    ? 'All Clients'
    : customerNameFilter;

  const selectedProjectStatusName = projectStatusFilter === 'all'
    ? 'All Statuses'
    : projectStatusFilter;

  const selectedProjectHeadName = projectHeadFilter === 'all'
    ? 'All Heads'
    : projectHeadFilter;

  return (
    <div style={projectListStyle} className="p-0 sm:p-4 md:p-8">
      <div style={backgroundOverlayStyle}></div>
      <div style={contentWrapperStyle}>
        {/* Modern Glassmorphism Header */}
        {/* Project Status Modal preserved while removing header */}
        {showStatusModal && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100
          }}>
            <div style={{ backgroundColor: 'white', borderRadius: '1rem', padding: '1.5rem', width: '95%', maxWidth: 640, fontSize: '0.95rem', whiteSpace: 'normal' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', minWidth: 0 }}>
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, whiteSpace: 'nowrap' }}>Change Project Status</h3>
                <button onClick={() => setShowStatusModal(false)} style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.35rem 0.65rem', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600 }}>Close</button>
              </div>

              <div style={{ display: 'grid', gap: '0.9rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: '0.95rem' }}>Status</label>
                  <select
                    value={statusForm.status}
                    onChange={(e) => setStatusForm(prev => ({ ...prev, status: e.target.value }))}
                    style={{ width: '100%', padding: '0.6rem 0.7rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', background: 'white', fontSize: '0.95rem', boxSizing: 'border-box' }}
                  >
                    <option value="">-- Select Status --</option>
                    <option value="Pending">Pending</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="Overdue">Overdue</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: '0.95rem' }}>Description</label>
                  <textarea
                    value={statusForm.description}
                    onChange={(e) => setStatusForm(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                    style={{ width: '100%', padding: '0.6rem 0.7rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', resize: 'vertical', fontSize: '0.95rem', lineHeight: 1.5, boxSizing: 'border-box', fontWeight: 400 }}
                    placeholder="Write something about this status update..."
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: '0.95rem' }}>Attachment (max 5 MB each)</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <input id="project-status-files" type="file" multiple accept=".jpg,.jpeg,.pdf,.docx,.txt" onChange={handleStatusFileChange} style={{ display: 'none' }} />
                    <label htmlFor="project-status-files" style={{ padding: '0.5rem 0.9rem', borderRadius: '0.5rem', background: '#111827', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' }}>Choose Files</label>
                    <div style={{ color: '#374151', fontSize: '0.9rem' }}>
                      {statusForm.files && statusForm.files.length > 0 ? `${statusForm.files.length} file(s) selected` : 'No file chosen'}
                    </div>
                  </div>
                  {statusForm.files && statusForm.files.length > 0 && (
                    <ul style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#374151', paddingLeft: '1rem' }}>
                      {statusForm.files.map((f, i) => (
                        <li key={i}>{f.name} ({Math.round(f.size / 1024)} KB)</li>
                      ))}
                    </ul>
                  )}
                  <div style={{ marginTop: 6, color: '#6b7280', fontSize: '0.85rem' }}>
                    Allowed: JPG, JPEG, PDF, DOCX, TXT. Max size 2 MB per file. Stored in secondary space (to be configured).
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', marginTop: '0.5rem' }}>
                  <button onClick={() => setShowStatusModal(false)} style={{ padding: '0.55rem 1rem', borderRadius: '0.5rem', border: '1px solid #e5e7eb', background: 'white', cursor: 'pointer', fontSize: '0.95rem' }}>Cancel</button>
                  <button onClick={submitStatusChange} style={{ padding: '0.55rem 1rem', borderRadius: '0.5rem', background: '#2563eb', color: 'white', border: 'none', cursor: 'pointer', fontSize: '0.95rem', fontWeight: 600 }}>Update</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Merged Filter & Select Section */}
        <div className="bg-white/80 backdrop-blur-lg rounded-none sm:rounded-2xl p-3 sm:p-6 shadow-md sm:shadow-xl border-y sm:border border-gray-200/50 mb-3 sm:mb-8 relative z-20">
          {/* Decorative background element */}
          <div className="absolute top-0 right-0 -mr-16 -mt-16 w-32 h-32 rounded-full bg-blue-50 opacity-50 blur-2xl"></div>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6 relative z-10">
            <h3 className="m-0 text-base sm:text-xl font-bold text-gray-800 flex items-center gap-2">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg text-white shadow-md">
                <Search size={18} />
              </div>
              Search, Select & Filter Projects
              {selectedProjects.length > 0 && (
                <span className="text-xs font-bold text-blue-700 bg-blue-100 px-3 py-1 rounded-full border border-blue-200">
                  {selectedProjects.length} selected
                </span>
              )}
            </h3>
            <div className="flex flex-wrap items-center gap-3">
              {selectedProjects.length > 0 && (
                <button
                  onClick={() => setSelectedProjects([])}
                  className="flex-1 sm:flex-none px-4 py-2.5 bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 rounded-xl transition-all font-semibold text-sm"
                >
                  Clear Selection
                </button>
              )}

              <button
                onClick={() => {
                  setDepartmentFilter('all');
                  setCustomerNameFilter('all');
                  setProjectNameFilter('all');
                  setCityFilter('all');
                  setStateFilter('all');
                  setProjectHeadFilter('all');
                  setProjectStatusFilter('all');
                }}
                className="flex-1 sm:flex-none px-4 py-2.5 bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200 rounded-xl transition-all font-semibold text-sm"
              >
                Reset Filters
              </button>
              <button
                onClick={() => onNavigate ? onNavigate('/projects/add', 'Add Project') : navigate('/projects/add')}
                className="flex-1 sm:flex-none px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl transition-all font-semibold text-sm shadow-md flex items-center justify-center gap-2"
              >
                <Plus size={16} />
                <span className="hidden sm:inline whitespace-nowrap">Create New Project</span>
              </button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-4 flex-wrap" style={{
            marginBottom: showProjectSelector ? '1.5rem' : '0'
          }}>
            {/* Search Bar - Integrated */}
            <div className="w-full sm:flex-[2_1_300px]">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Search Projects</label>
              <input
                type="text"
                placeholder="Search name, status, dept, client..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full h-9 sm:h-[42px] px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-md sm:rounded-lg outline-none focus:ring-2 focus:ring-blue-500 box-border"
              />
            </div>

            {/* Department Filter */}
            <div className={`w-full sm:flex-[1_1_180px] relative custom-dropdown-container ${activeDropdown === 'department' ? 'z-30' : 'z-10'}`}>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Department</label>
              <div
                onClick={() => setActiveDropdown(activeDropdown === 'department' ? null : 'department')}
                className="w-full h-9 sm:h-[42px] px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-md sm:rounded-lg outline-none bg-white flex items-center justify-between cursor-pointer focus:ring-2 focus:ring-blue-500 hover:border-gray-400 transition-colors box-border"
              >
                <span className="truncate text-gray-800 font-medium">{selectedDepartmentName}</span>
                <ChevronDown size={16} className={`text-gray-500 transition-transform duration-200 ${activeDropdown === 'department' ? 'rotate-180' : ''}`} />
              </div>
              {activeDropdown === 'department' && (
                <div className="absolute left-0 right-0 mt-1 max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-md sm:rounded-lg shadow-lg z-50 py-1">
                  <div
                    onClick={() => { setDepartmentFilter('all'); setActiveDropdown(null); }}
                    className={`px-3 py-2 text-xs sm:text-sm cursor-pointer hover:bg-gray-50 transition-colors ${departmentFilter === 'all' ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                  >
                    All Departments
                  </div>
                  {departmentsLoading ? (
                    <div className="px-3 py-2 text-xs sm:text-sm text-gray-400">Loading...</div>
                  ) : (
                    departments.map(dept => {
                      const deptVal = dept._id || dept.id;
                      const isSelected = String(departmentFilter) === String(deptVal);
                      return (
                        <div
                          key={deptVal}
                          onClick={() => { setDepartmentFilter(deptVal); setActiveDropdown(null); }}
                          className={`px-3 py-2 text-xs sm:text-sm cursor-pointer hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                        >
                          {dept.name}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            {/* Client Name Filter */}
            <div className={`w-full sm:flex-[1_1_180px] relative custom-dropdown-container ${activeDropdown === 'client' ? 'z-30' : 'z-10'}`}>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Client Name</label>
              <div
                onClick={() => setActiveDropdown(activeDropdown === 'client' ? null : 'client')}
                className="w-full h-9 sm:h-[42px] px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-md sm:rounded-lg outline-none bg-white flex items-center justify-between cursor-pointer focus:ring-2 focus:ring-blue-500 hover:border-gray-400 transition-colors box-border"
              >
                <span className="truncate text-gray-800 font-medium">{selectedClientName}</span>
                <ChevronDown size={16} className={`text-gray-500 transition-transform duration-200 ${activeDropdown === 'client' ? 'rotate-180' : ''}`} />
              </div>
              {activeDropdown === 'client' && (
                <div className="absolute left-0 right-0 mt-1 max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-md sm:rounded-lg shadow-lg z-50 py-1">
                  <div
                    onClick={() => { setCustomerNameFilter('all'); setActiveDropdown(null); }}
                    className={`px-3 py-2 text-xs sm:text-sm cursor-pointer hover:bg-gray-50 transition-colors ${customerNameFilter === 'all' ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                  >
                    All Clients
                  </div>
                  {filterOptions.customerNames.map(customer => {
                    const isSelected = customerNameFilter === customer;
                    return (
                      <div
                        key={customer}
                        onClick={() => { setCustomerNameFilter(customer); setActiveDropdown(null); }}
                        className={`px-3 py-2 text-xs sm:text-sm cursor-pointer hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                      >
                        {customer}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Project Status Filter */}
            <div className={`w-full sm:flex-[1_1_180px] relative custom-dropdown-container ${activeDropdown === 'status' ? 'z-30' : 'z-10'}`}>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">📊 Project Status</label>
              <div
                onClick={() => setActiveDropdown(activeDropdown === 'status' ? null : 'status')}
                className="w-full h-9 sm:h-[42px] px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-md sm:rounded-lg outline-none bg-white flex items-center justify-between cursor-pointer focus:ring-2 focus:ring-blue-500 hover:border-gray-400 transition-colors box-border"
              >
                <span className="truncate text-gray-800 font-medium">{selectedProjectStatusName}</span>
                <ChevronDown size={16} className={`text-gray-500 transition-transform duration-200 ${activeDropdown === 'status' ? 'rotate-180' : ''}`} />
              </div>
              {activeDropdown === 'status' && (
                <div className="absolute left-0 right-0 mt-1 max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-md sm:rounded-lg shadow-lg z-50 py-1">
                  <div
                    onClick={() => { setProjectStatusFilter('all'); setActiveDropdown(null); }}
                    className={`px-3 py-2 text-xs sm:text-sm cursor-pointer hover:bg-gray-50 transition-colors ${projectStatusFilter === 'all' ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                  >
                    All Statuses
                  </div>
                  {statusOptions.map(status => {
                    const isSelected = projectStatusFilter === status;
                    return (
                      <div
                        key={status}
                        onClick={() => { setProjectStatusFilter(status); setActiveDropdown(null); }}
                        className={`px-3 py-2 text-xs sm:text-sm cursor-pointer hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                      >
                        {status}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Project Head Filter */}
            <div className={`w-full sm:flex-[1_1_180px] relative custom-dropdown-container ${activeDropdown === 'head' ? 'z-30' : 'z-10'}`}>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">💼 Project Head</label>
              <div
                onClick={() => setActiveDropdown(activeDropdown === 'head' ? null : 'head')}
                className="w-full h-9 sm:h-[42px] px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-md sm:rounded-lg outline-none bg-white flex items-center justify-between cursor-pointer focus:ring-2 focus:ring-blue-500 hover:border-gray-400 transition-colors box-border"
              >
                <span className="truncate text-gray-800 font-medium">{selectedProjectHeadName}</span>
                <ChevronDown size={16} className={`text-gray-500 transition-transform duration-200 ${activeDropdown === 'head' ? 'rotate-180' : ''}`} />
              </div>
              {activeDropdown === 'head' && (
                <div className="absolute left-0 right-0 mt-1 max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-md sm:rounded-lg shadow-lg z-50 py-1">
                  <div
                    onClick={() => { setProjectHeadFilter('all'); setActiveDropdown(null); }}
                    className={`px-3 py-2 text-xs sm:text-sm cursor-pointer hover:bg-gray-50 transition-colors ${projectHeadFilter === 'all' ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                  >
                    All Heads
                  </div>
                  {filterOptions.projectHeads.map(head => {
                    const isSelected = projectHeadFilter === head;
                    return (
                      <div
                        key={head}
                        onClick={() => { setProjectHeadFilter(head); setActiveDropdown(null); }}
                        className={`px-3 py-2 text-xs sm:text-sm cursor-pointer hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                      >
                        {head}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {showProjectSelector && (
            <div style={{ marginTop: '1.5rem', borderTop: '1px solid #e5e7eb', paddingTop: '1.5rem' }}>
              <div style={{ marginBottom: '1rem' }}>
                <button
                  onClick={() => {
                    const baseFilteredProjects = getFilteredProjects();
                    if (selectedProjects.length === baseFilteredProjects.length && baseFilteredProjects.length > 0) {
                      setSelectedProjects([]);
                    } else {
                      setSelectedProjects(baseFilteredProjects.map(p => p._id || p.id));
                    }
                  }}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: (() => {
                      const baseFilteredProjects = getFilteredProjects();
                      return selectedProjects.length === baseFilteredProjects.length && baseFilteredProjects.length > 0 ? '#10b981' : '#6b7280';
                    })(),
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                >
                  {(() => {
                    const baseFilteredProjects = getFilteredProjects();
                    return selectedProjects.length === baseFilteredProjects.length && baseFilteredProjects.length > 0
                      ? 'Deselect All' : 'Select All';
                  })()}
                </button>
              </div>

              <div style={{
                maxHeight: '300px',
                overflowY: 'auto',
                border: '1px solid #e5e7eb',
                borderRadius: '0.5rem',
                backgroundColor: '#f9fafb'
              }}>
                {(() => {
                  const baseFilteredProjects = getFilteredProjects();
                  return baseFilteredProjects.length === 0 ? (
                    <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>No projects found</div>
                  ) : (
                    baseFilteredProjects.map(project => (
                      <div
                        key={project._id || project.id}
                        onClick={() => {
                          const projectId = project._id || project.id;
                          setSelectedProjects(prev => prev.includes(projectId) ? prev.filter(id => id !== projectId) : [...prev, projectId]);
                        }}
                        style={{
                          padding: '1rem',
                          borderBottom: '1px solid #e5e7eb',
                          cursor: 'pointer',
                          backgroundColor: selectedProjects.includes(project._id || project.id) ? '#dbeafe' : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem'
                        }}
                      >
                        <input type="checkbox" checked={selectedProjects.includes(project._id || project.id)} onChange={() => { }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 500, color: '#374151' }}>{project.title || project.name}</div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Status: {project.status || 'Unknown'} | Budget: ₹{(project.budget || 0).toLocaleString('en-IN')}</div>
                        </div>
                      </div>
                    ))
                  );
                })()}
              </div>
            </div>
          )}
        </div>

        {/* Statistics Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4 mb-4 sm:mb-8 relative z-10">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl sm:rounded-2xl p-2.5 sm:p-4 shadow-sm sm:shadow-lg text-white flex flex-row sm:flex-col items-center sm:items-start justify-start sm:justify-between gap-2.5 sm:gap-2 transform sm:transition-transform sm:hover:-translate-y-1">
            <div className="bg-white/20 p-1.5 sm:p-2 rounded-lg flex-shrink-0">
              <TrendingUp size={16} className="text-white sm:w-[18px] sm:h-[18px]" />
            </div>
            <div>
              <div className="text-base sm:text-3xl font-extrabold leading-tight">{localProjects.length}</div>
              <div className="text-blue-100 text-[10px] sm:text-sm font-medium">Total Projects</div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl sm:rounded-2xl p-2.5 sm:p-4 shadow-sm sm:shadow-lg text-white flex flex-row sm:flex-col items-center sm:items-start justify-start sm:justify-between gap-2.5 sm:gap-2 transform sm:transition-transform sm:hover:-translate-y-1">
            <div className="bg-white/20 p-1.5 sm:p-2 rounded-lg flex-shrink-0">
              <Zap size={16} className="text-white sm:w-[18px] sm:h-[18px]" />
            </div>
            <div>
              <div className="text-base sm:text-3xl font-extrabold leading-tight">
                {localProjects.filter(p => normalizeStatus(p.status) === 'In Progress').length}
              </div>
              <div className="text-blue-100 text-[10px] sm:text-sm font-medium">In Progress</div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl sm:rounded-2xl p-2.5 sm:p-4 shadow-sm sm:shadow-lg text-white flex flex-row sm:flex-col items-center sm:items-start justify-start sm:justify-between gap-2.5 sm:gap-2 transform sm:transition-transform sm:hover:-translate-y-1">
            <div className="bg-white/20 p-1.5 sm:p-2 rounded-lg flex-shrink-0">
              <Star size={16} className="text-white sm:w-[18px] sm:h-[18px]" />
            </div>
            <div>
              <div className="text-base sm:text-3xl font-extrabold leading-tight">
                {localProjects.filter(p => normalizeStatus(p.status) === 'Completed').length}
              </div>
              <div className="text-blue-100 text-[10px] sm:text-sm font-medium">Completed</div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl sm:rounded-2xl p-2.5 sm:p-4 shadow-sm sm:shadow-lg text-white flex flex-row sm:flex-col items-center sm:items-start justify-start sm:justify-between gap-2.5 sm:gap-2 transform sm:transition-transform sm:hover:-translate-y-1">
            <div className="bg-white/20 p-1.5 sm:p-2 rounded-lg flex-shrink-0">
              <IndianRupee size={16} className="text-white sm:w-[18px] sm:h-[18px]" />
            </div>
            <div>
              <div className="text-base sm:text-2xl font-extrabold leading-tight truncate" title={`₹${localProjects.reduce((sum, p) => sum + getBudget(p), 0).toLocaleString('en-IN')}`}>
                ₹{localProjects.reduce((sum, p) => sum + getBudget(p), 0).toLocaleString('en-IN', { notation: "compact", compactDisplay: "short" })}
              </div>
              <div className="text-blue-100 text-[10px] sm:text-sm font-medium">Total Budget</div>
            </div>
          </div>
        </div>


        {/* Attractive Project Table Section */}
        {filteredProjects.length === 0 ? (
          <div style={{
            textAlign: "center",
            padding: "4rem 2rem",
            background: 'white',
            borderRadius: '1.5rem',
            border: '1px solid #e5e7eb',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
          }}>
            <div style={{ fontSize: "4rem", marginBottom: "1.5rem", opacity: 0.7 }}></div>
            <h3 style={{ fontSize: "1.8rem", color: "#1f2937", marginBottom: "1rem", fontWeight: 700 }}>
              {(projects || []).length === 0 ? "🚀 No projects yet" : "No projects match your criteria"}
            </h3>
            <p style={{ color: "#6b7280", marginBottom: "2rem", fontSize: "1.1rem" }}>
              {(projects || []).length === 0 ? "Create your first project to get started." : "Try adjusting your search or filters."}
            </p>
          </div>
        ) : (
          <div>
            <div className="hidden md:block" style={{
              backgroundColor: 'white',
              borderRadius: '1rem',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb',
              overflowX: 'auto',
              animation: isLoaded ? "fadeInUp 0.8s ease-out both" : "none"
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #f3f4f6' }}>
                    <th style={{ padding: '1.25rem 1rem', fontSize: '0.85rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', textAlign: 'left', letterSpacing: '0.025em' }}>Project Name</th>
                    <th style={{ padding: '1.25rem 1rem', fontSize: '0.85rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', textAlign: 'left', letterSpacing: '0.025em' }}>Status</th>
                    <th style={{ padding: '1.25rem 1rem', fontSize: '0.85rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', textAlign: 'left', letterSpacing: '0.025em' }}>Department</th>
                    <th style={{ padding: '1.25rem 1rem', fontSize: '0.85rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', textAlign: 'left', letterSpacing: '0.025em' }}>Progress</th>
                    <th style={{ padding: '1.25rem 1rem', fontSize: '0.85rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', textAlign: 'left', letterSpacing: '0.025em' }}>Due Date</th>
                    <th style={{ padding: '1.25rem 1rem', fontSize: '0.85rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', textAlign: 'left', letterSpacing: '0.025em' }}>Budget</th>
                    <th style={{ padding: '1.25rem 1rem', fontSize: '0.85rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', textAlign: 'center', letterSpacing: '0.025em' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjects.map((project, index) => {
                    const id = getId(project);
                    const title = getTitle(project);
                    const status = normalizeStatus(project?.status);
                    const due = getDueDate(project);
                    const budget = getBudget(project);
                    const dept = getDepartment(project);
                    const progressData = getStatusProgress(project?.status, statusOptions);
                    const progress = progressData.percentage;

                    return (
                      <tr
                        key={id || index}
                        onClick={() => handleCardClick(id)}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        style={{
                          borderBottom: '1px solid #f3f4f6',
                          transition: 'background-color 0.2s ease',
                          cursor: 'pointer'
                        }}
                      >
                        <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                          <div style={{ fontWeight: 600, color: '#1f2937', fontSize: '0.95rem' }}>{title}</div>

                        </td>
                        <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                          <span style={{
                            display: "inline-block",
                            padding: "0.4rem 0.8rem",
                            borderRadius: "0.5rem",
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            backgroundColor: getStatusColor(project?.status),
                            color: "white"
                          }}>
                            {status}
                          </span>
                        </td>
                        <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#4b5563', fontSize: '0.875rem' }}>
                            <span style={{ fontSize: '1.1rem' }}></span>
                            {dept || 'General'}
                          </div>
                        </td>
                        <td style={{ padding: '1rem', verticalAlign: 'middle', minWidth: '150px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '0.75rem', fontWeight: 600 }}>
                            <span>{progress}%</span>
                          </div>
                          <div style={{ width: '100%', height: '6px', backgroundColor: '#f1f5f9', borderRadius: '10px', overflow: 'hidden' }}>
                            <div style={{ width: `${progress}%`, height: '100%', backgroundColor: '#6366f1', borderRadius: '10px', transition: 'width 0.8s ease-out' }} />
                          </div>
                        </td>
                        <td style={{ padding: '1rem', verticalAlign: 'middle', color: '#4b5563', fontSize: '0.875rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span>…</span>
                            {due ? due.toLocaleDateString('en-GB') : "N/A"}
                          </div>
                        </td>
                        <td style={{ padding: '1rem', verticalAlign: 'middle', color: '#1f2937', fontWeight: 600, fontSize: '0.875rem' }}>
                          ₹{budget ? budget.toLocaleString('en-IN') : "0"}
                        </td>
                        <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                          <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center" }} onClick={e => e.stopPropagation()}>
                            <button
                              onClick={() => openStatusModal(id, status)}
                              style={{ padding: '0.5rem', borderRadius: '0.5rem', border: '1px solid #d1d5db', backgroundColor: 'white', color: '#3b82f6', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
                              onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#eff6ff'; e.currentTarget.style.borderColor = '#3b82f6'; }}
                              onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'white'; e.currentTarget.style.borderColor = '#d1d5db'; }}
                              title="Quick Status Update"
                            >
                              <BarChart size={16} />
                            </button>
                            <button
                              onClick={() => {
                                const dueTasks = getDueMilestones(id, getTitle(project));
                                setSelectedProjectNotifications(dueTasks);
                                setShowNotificationModal(true);
                              }}
                              style={{ padding: '0.4rem', borderRadius: '0.5rem', border: '1px solid #d1d5db', backgroundColor: '#fff7ed', color: '#f59e0b', cursor: 'pointer', position: 'relative' }}
                              title="Notifications"
                            >
                              <Bell size={16} />
                            </button>
                            <button
                              onClick={() => onNavigate ? onNavigate(`/edit-project/${id}`, 'Edit Project') : navigate(`/projects/edit/${id}`)}
                              style={{ padding: '0.4rem', borderRadius: '0.5rem', border: '1px solid #d1d5db', backgroundColor: '#f0fdf4', color: '#10b981', cursor: 'pointer' }}
                              title="Edit"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => deleteProject(id)}
                              style={{ padding: '0.4rem', borderRadius: '0.5rem', border: '1px solid #d1d5db', backgroundColor: '#fef2f2', color: '#ef4444', cursor: 'pointer' }}
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Mobile Cards */}
            <div className="md:hidden flex flex-col gap-2 mb-8">
              {filteredProjects.map((project, index) => {
                const id = getId(project);
                const title = getTitle(project);
                const status = normalizeStatus(project?.status);
                const due = getDueDate(project);
                const budget = getBudget(project);
                const dept = getDepartment(project);
                const progressData = getStatusProgress(project?.status, statusOptions);
                const progress = progressData.percentage;

                return (
                  <div
                    key={id || index}
                    onClick={() => handleCardClick(id)}
                    className="bg-white rounded-none sm:rounded-xl p-3 shadow-sm border-y sm:border border-gray-200 flex flex-col gap-2 sm:gap-3 active:scale-[0.98] transition-transform"
                  >
                    <div className="flex justify-between items-start">
                      <div className="font-bold text-gray-900 text-base sm:text-lg leading-tight">
                        {title}
                      </div>
                      <span style={{
                        display: "inline-block",
                        padding: "0.15rem 0.4rem",
                        borderRadius: "0.375rem",
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        backgroundColor: getStatusColor(project?.status),
                        color: "white"
                      }}>
                        {status}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-1.5 text-xs sm:text-sm text-gray-600">
                      <div><span className="font-medium text-gray-500">Dept:</span> {dept || 'General'}</div>
                      <div><span className="font-medium text-gray-500">Due:</span> {due ? due.toLocaleDateString('en-GB') : "N/A"}</div>
                      <div className="col-span-2 font-bold text-gray-800 text-sm mt-0.5">₹{budget ? budget.toLocaleString('en-IN') : "0"}</div>
                    </div>

                    <div className="w-full mt-1">
                      <div className="flex justify-between mb-1 text-[10px] sm:text-xs font-semibold text-gray-600">
                        <span>Progress</span>
                        <span>{progress}%</span>
                      </div>
                      <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div style={{ width: `${progress}%` }} className="h-full bg-indigo-500 rounded-full"></div>
                      </div>
                    </div>

                    <div className="flex justify-end gap-1.5 mt-1 pt-2 border-t border-gray-100" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => openStatusModal(id, status)}
                        className="p-1.5 rounded-md border border-gray-300 bg-white text-blue-500 hover:bg-blue-50 transition-colors"
                        title="Update Status"
                      >
                        <BarChart size={14} />
                      </button>
                      <button
                        onClick={() => {
                          const dueTasks = getDueMilestones(id, getTitle(project));
                          setSelectedProjectNotifications(dueTasks);
                          setShowNotificationModal(true);
                        }}
                        className="p-1.5 rounded-md border border-orange-200 bg-orange-50 text-orange-500 hover:bg-orange-100 transition-colors"
                        title="Notifications"
                      >
                        <Bell size={14} />
                      </button>
                      <button
                        onClick={() => onNavigate ? onNavigate(`/edit-project/${id}`, 'Edit Project') : navigate(`/projects/edit/${id}`)}
                        className="p-1.5 rounded-md border border-green-200 bg-green-50 text-green-500 hover:bg-green-100 transition-colors"
                        title="Edit"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => deleteProject(id)}
                        className="p-1.5 rounded-md border border-red-200 bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>

      {/* Notification Modal */}
      {(() => {
        console.log('Modal render check - showNotificationModal:', showNotificationModal);
        console.log(' selectedProjectNotifications:', selectedProjectNotifications);
        return showNotificationModal;
      })() && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '1rem',
              padding: '2rem',
              width: '90%',
              maxWidth: '600px',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ color: '#1f2937', margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>
                  🔔 Due & Upcoming Project-Tasks
                </h2>
                <button
                  onClick={() => setShowNotificationModal(false)}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: 600
                  }}
                >
                  Close
                </button>
              </div>

              <div style={{ display: 'grid', gap: '1rem' }}>
                {selectedProjectNotifications.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    color: '#6b7280',
                    padding: '2rem',
                    backgroundColor: '#f9fafb',
                    borderRadius: '0.5rem',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
                    <h3 style={{ margin: '0 0 0.5rem 0', color: '#1f2937' }}>No Upcoming Project-Tasks</h3>
                    <p style={{ margin: 0 }}>All Project-Tasks are on track! No deadlines today or tomorrow.</p>
                  </div>
                ) : (
                  selectedProjectNotifications.map((task, index) => {
                    const dueDate = new Date(task.dueDate);
                    dueDate.setHours(0, 0, 0, 0); // Set due date to start of day for comparison
                    const today = new Date();
                    today.setHours(0, 0, 0, 0); // Set today to start of day for comparison
                    const daysUntil = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
                    const isOverdue = daysUntil < 0;
                    const isDueToday = daysUntil === 0;
                    const isDueTomorrow = daysUntil === 1;
                    const isUrgent = isOverdue || isDueToday;

                    console.log(`… Date comparison for "${task.title}":`, {
                      dueDate: task.dueDate,
                      dueDateNormalized: dueDate,
                      todayNormalized: today,
                      daysUntil,
                      isOverdue,
                      isDueToday,
                      isUrgent
                    });

                    return (
                      <div
                        key={task._id || index}
                        onClick={() => {
                          // Navigate to project view and close modal
                          console.log('🚀 Milestone card clicked:', task);
                          let projectId = null;

                          // Try different ways to get project ID
                          if (task.project && typeof task.project === 'object' && task.project._id) {
                            projectId = task.project._id;
                            console.log('Found project ID from object:', projectId);
                          } else if (task.project && typeof task.project === 'string') {
                            projectId = task.project;
                            console.log('Found project ID from string:', projectId);
                          } else if (task.projectId) {
                            projectId = task.projectId;
                            console.log('Found project ID from projectId field:', projectId);
                          } else if (task.projectName) {
                            // Fallback: try to find project by name
                            const project = localProjects.find(p =>
                              getTitle(p) === task.projectName ||
                              p.name === task.projectName
                            );
                            if (project) {
                              projectId = getId(project);
                              console.log('Found project ID by name:', projectId);
                            }
                          }

                          if (projectId) {
                            if (onNavigate) {
                              onNavigate(`/projects/${projectId}`, 'Project Details');
                            } else {
                              navigate(`/projects/${projectId}`);
                            }
                            setShowNotificationModal(false);
                          } else {
                            console.warn('⚠️ Could not determine project ID for milestone:', task);
                          }
                        }}
                        style={{
                          padding: '1.5rem',
                          border: isUrgent ? '2px solid #ef4444' : '1px solid #e5e7eb',
                          borderRadius: '0.75rem',
                          backgroundColor: isUrgent ? '#fef2f2' : '#f9fafb',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                          cursor: (task.projectId || task.projectName) ? 'pointer' : 'default',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (task.projectId || task.projectName) {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (task.projectId || task.projectName) {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                          }
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                          <div>
                            <h3 style={{ margin: '0 0 0.5rem 0', color: '#1f2937', fontSize: '1.1rem', fontWeight: 600 }}>
                              {task.title}
                            </h3>
                            <p style={{ margin: '0 0 0.5rem 0', color: '#6b7280', fontSize: '0.9rem' }}>
                              📋 Project: {getProjectNameForMilestone(task)}
                            </p>
                            <p style={{ margin: '0 0 0.5rem 0', color: '#6b7280', fontSize: '0.9rem' }}>
                              Assigned to: <span style={{ color: '#1f2937', fontWeight: 600 }}>{getEmployeeName(task.assignedTeamMember)}</span>
                            </p>
                            <p style={{ margin: '0.25rem 0 0 0', color: '#3b82f6', fontSize: '0.75rem', fontWeight: 500 }}>
                              † Click to view project details
                            </p>
                          </div>
                          {(isUrgent || isDueTomorrow) && (
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              backgroundColor: isOverdue ? '#dc2626' : isDueToday ? '#ef4444' : '#f59e0b',
                              color: 'white',
                              borderRadius: '1rem',
                              fontSize: '0.75rem',
                              fontWeight: 700,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem'
                            }}>
                              {isOverdue ? 'š OVERDUE' : isDueToday ? '⚠️ DUE TODAY' : isDueTomorrow ? ' DUE TOMORROW' : '… UPCOMING'}
                            </span>
                          )}
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#6b7280' }}>
                            <Calendar size={16} />
                            <span style={{ fontSize: '0.9rem' }}>
                              Due: {dueDate.toLocaleDateString('en-GB')}
                            </span>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '1rem',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              backgroundColor: task.status === 'In Progress' ? '#dbeafe' : '#fef3c7',
                              color: task.status === 'In Progress' ? '#1e40af' : '#92400e'
                            }}>
                              {task.status}
                            </span>

                            <span style={{
                              color: isUrgent ? '#ef4444' : '#f59e0b',
                              fontWeight: 700,
                              fontSize: '0.85rem'
                            }}>
                              {daysUntil < 0 ? `š ${Math.abs(daysUntil)} days overdue` :
                                daysUntil === 0 ? '… Due Today!' :
                                  daysUntil === 1 ? ' Due Tomorrow!' :
                                    `—ï ${daysUntil} days left`}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default ProjectList;

